import React, { useState, useEffect, useRef } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../assets/css/formulario.css';
import CustomNavbar from './Navbar/Navbar';

const SistemaQuejas = () => {
    const [formData, setFormData] = useState({
        tipoQueja: '',
        nombre: '',
        correo: '',
        proyecto: '',
        cargo: '',
        queja: '',
        evidencia: null
    });

    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState({});
    const [showSuccess, setShowSuccess] = useState(false);
    const [confirmationSentToUser, setConfirmationSentToUser] = useState(false);
    const [fileError, setFileError] = useState('');
    const [hasSubmitted, setHasSubmitted] = useState(false);
    const [tipoEnviado, setTipoEnviado] = useState('');


    const tipoQuejaRef = useRef(null);
    const nombreRef = useRef(null);
    const correoRef = useRef(null);
    const proyectoRef = useRef(null);
    const cargoRef = useRef(null);
    const quejaRef = useRef(null);
    const termsRef = useRef(null);

    useEffect(() => {
        if (showSuccess) {
            const timer = setTimeout(() => {
                setShowSuccess(false);
                setConfirmationSentToUser(false);
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [showSuccess]);

    const validateEmail = (email) => {
        if (!email) return 'Este campo es obligatorio';
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            return 'Ingrese un correo válido';
        }
        return '';
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.tipoQueja) {
            newErrors.tipoQueja = `El tipo de ${getTipoTexto()} es requerido`;
        }

        if (!formData.queja || formData.queja.length < 10) {
            newErrors.queja = formData.queja
                ? 'La descripción debe tener al menos 10 caracteres'
                : 'Este campo es obligatorio';
        }

        if (formData.tipoQueja === 'identificada') {
            const fieldsToValidate = [
                { name: 'nombre', label: 'Nombre completo' },
                { name: 'correo', label: 'Correo electrónico' },
                { name: 'proyecto', label: 'Proyecto' },
                { name: 'cargo', label: 'Cargo' }
            ];

            fieldsToValidate.forEach(field => {
                if (!formData[field.name] || formData[field.name].toString().trim() === '') {
                    newErrors[field.name] = 'Este campo es obligatorio';
                } else if (field.name === 'correo') {
                    const emailError = validateEmail(formData.correo);
                    if (emailError) {
                        newErrors.correo = emailError;
                    }
                }
            });
        }

        if (!acceptedTerms) {
            newErrors.terms = 'Debe aceptar los términos y condiciones';
        }

        return newErrors;
    };

    const scrollToFirstError = (errorFields) => {
        const errorRefs = {
            tipoQueja: tipoQuejaRef,
            nombre: nombreRef,
            correo: correoRef,
            proyecto: proyectoRef,
            cargo: cargoRef,
            queja: quejaRef,
            terms: termsRef
        };

        const firstErrorField = Object.keys(errorFields)[0];
        if (firstErrorField && errorRefs[firstErrorField] && errorRefs[firstErrorField].current) {
            errorRefs[firstErrorField].current.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
            });

            const element = errorRefs[firstErrorField].current;
            element.classList.add('error-highlight');
            setTimeout(() => {
                element.classList.remove('error-highlight');
            }, 2000);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, files } = e.target;

        if (type === 'file') {
            if (files && files.length > 0) {
                const file = files[0];
                const maxSize = 5 * 1024 * 1024;

                if (file.size > maxSize) {
                    setFileError('El archivo es demasiado grande. El tamaño máximo permitido es 5 MB.');
                    setFormData(prev => ({
                        ...prev,
                        [name]: null
                    }));
                    e.target.value = '';
                } else {
                    setFileError('');
                    setFormData(prev => ({
                        ...prev,
                        [name]: file
                    }));
                }
            } else {
                setFileError('');
                setFormData(prev => ({
                    ...prev,
                    [name]: null
                }));
            }
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));

            if (hasSubmitted && errors[name]) {
                const newErrors = { ...errors };
                let fieldError = '';

                if (name === 'tipoQueja') {
                    if (!value) {
                        fieldError = `El tipo de ${getTipoTexto()} es requerido`;
                    }
                } else if (name === 'queja') {
                    if (!value || value.length < 10) {
                        fieldError = value ? 'La descripción debe tener al menos 10 caracteres' : 'Este campo es obligatorio';
                    }
                } else if (name === 'correo') {
                    const emailError = validateEmail(value);
                    fieldError = emailError;
                } else if (['nombre', 'proyecto', 'cargo'].includes(name)) {
                    if (!value || value.toString().trim() === '') {
                        fieldError = 'Este campo es obligatorio';
                    }
                }

                if (fieldError) {
                    newErrors[name] = fieldError;
                } else {
                    delete newErrors[name];
                }

                setErrors(newErrors);
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setHasSubmitted(true);

        const formErrors = validateForm();

        if (Object.keys(formErrors).length > 0 || fileError) {
            setErrors(formErrors);

            if (Object.keys(formErrors).length > 0) {
                setTimeout(() => {
                    scrollToFirstError(formErrors);
                }, 100);
            }

            return;
        }

        setIsSubmitting(true);

        try {
            const formDataToSend = new FormData();

            formDataToSend.append('tipoQueja', formData.tipoQueja);
            formDataToSend.append('queja', formData.queja);
            if (formData.tipoQueja === 'identificada') {
                formDataToSend.append('nombre', formData.nombre);
                formDataToSend.append('correo', formData.correo);
                formDataToSend.append('proyecto', formData.proyecto);
                formDataToSend.append('cargo', formData.cargo);
            }

            if (formData.evidencia) {
                formDataToSend.append('evidencia', formData.evidencia);
            }

            for (let [key, value] of formDataToSend.entries()) {
                console.log(`${key}:`, value);
            }

            const response = await fetch('https://api-sugerencias.conduto.com.ec/guardar-queja', {
                method: 'POST',
                body: formDataToSend
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || `Error al enviar la ${getTipoTexto()}`);
            }

            const result = await response.json();

            if (result.success) {
                const wasIdentificada = formData.tipoQueja === 'identificada';

                setTipoEnviado(formData.tipoQueja);
                setShowSuccess(true);
                setConfirmationSentToUser(wasIdentificada);


                setFormData({
                    tipoQueja: '',
                    nombre: '',
                    correo: '',
                    proyecto: '',
                    cargo: '',
                    queja: '',
                    evidencia: null
                });
                setAcceptedTerms(false);
                setErrors({});
                setFileError('');
                setHasSubmitted(false);

                const fileInput = document.querySelector('input[name="evidencia"]');
                if (fileInput) fileInput.value = '';
            } else {
                throw new Error(result.message);
            }

        } catch (error) {
            console.error(`Error al enviar la ${getTipoTexto()}:`, error);
            alert(`Error: ${error.message}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    const RequiredAsterisk = () => <span className="text-danger">*</span>;

    const hasVisibleErrors = () => {
        return hasSubmitted && (Object.keys(errors).length > 0 || fileError);
    };

    const getTipoTexto = () => {
        if (formData.tipoQueja === 'identificada') {
            return 'sugerencia';
        } else if (formData.tipoQueja === 'anonima') {
            return 'denuncia';
        } else {
            return 'sugerencia';
        }
    };

    return (
        <div className="container">
            <CustomNavbar />
            <div className="">
                <div className="card-header">
                    <h2 className="text-center mb-4">SISTEMA DE SUGERENCIAS</h2>
                </div>
                <div className="card-body">

                    <form onSubmit={handleSubmit} encType="multipart/form-data">

                        <div className="mb-4 formacion-item-content border p-3 mb-3 rounded" ref={tipoQuejaRef}>
                            <h4 className="border-bottom pb-2">TIPO DE SUGERENCIAS<RequiredAsterisk /></h4>
                            <div className="row">
                                <div className="col-md-12 mb-3">
                                    <label className="form-label">¿Cómo desea enviar su sugerencias?</label>
                                    <select
                                        className={`form-select ${hasSubmitted && errors.tipoQueja ? 'is-invalid' : ''}`}
                                        name="tipoQueja"
                                        value={formData.tipoQueja}
                                        onChange={handleInputChange}
                                    >
                                        <option value="">Seleccionar tipo</option>
                                        <option value="anonima">Denuncia - anónima</option>
                                        <option value="identificada">Sugerencia - con identificada</option>
                                    </select>
                                    {hasSubmitted && errors.tipoQueja && <div className="invalid-feedback">{errors.tipoQueja}</div>}

                                    {formData.tipoQueja === 'anonima' && (
                                        <div className="alert alert-info mt-3 text-start" role="alert">
                                            <strong>Al escoger esta opción, garantizamos que es completamente anónima y la información recibida se tratará con la máxima confidencialidad. </strong>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {formData.tipoQueja === 'identificada' && (
                            <div className="mb-4 formacion-item-content border p-3 mb-3 rounded">
                                <h4 className="border-bottom pb-2">INFORMACIÓN PERSONAL</h4>
                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label">Nombre completo <RequiredAsterisk /></label>
                                        <input
                                            type="text"
                                            className={`form-control ${hasSubmitted && errors.nombre ? 'is-invalid' : ''}`}
                                            name="nombre"
                                            value={formData.nombre}
                                            onChange={handleInputChange}
                                            placeholder="Ingrese su nombre completo"
                                            ref={nombreRef}
                                        />
                                        {hasSubmitted && errors.nombre && <div className="invalid-feedback">{errors.nombre}</div>}
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label">Correo electrónico <RequiredAsterisk /></label>
                                        <input
                                            type="email"
                                            className={`form-control ${hasSubmitted && errors.correo ? 'is-invalid' : ''}`}
                                            name="correo"
                                            value={formData.correo}
                                            onChange={handleInputChange}
                                            placeholder="usuario@conduto.com"
                                            ref={correoRef}
                                        />
                                        {hasSubmitted && errors.correo && <div className="invalid-feedback">{errors.correo}</div>}
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label">Proyecto <RequiredAsterisk /></label>
                                        <input
                                            type="text"
                                            className={`form-control ${hasSubmitted && errors.proyecto ? 'is-invalid' : ''}`}
                                            name="proyecto"
                                            value={formData.proyecto}
                                            onChange={handleInputChange}
                                            placeholder="Proyecto"
                                            ref={proyectoRef}
                                        />
                                        {hasSubmitted && errors.proyecto && <div className="invalid-feedback">{errors.proyecto}</div>}
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label">Cargo <RequiredAsterisk /></label>
                                        <input
                                            type="text"
                                            className={`form-control ${hasSubmitted && errors.cargo ? 'is-invalid' : ''}`}
                                            name="cargo"
                                            value={formData.cargo}
                                            onChange={handleInputChange}
                                            placeholder="Ej: Asistente, Analista, Supervisor"
                                            ref={cargoRef}
                                        />
                                        {hasSubmitted && errors.cargo && <div className="invalid-feedback">{errors.cargo}</div>}
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="mb-4 formacion-item-content border p-3 mb-3 rounded" ref={quejaRef}>
                            <h4 className="border-bottom pb-2">
                                {formData.tipoQueja === 'identificada' ? 'DESCRIPCIÓN DE LA SUGERENCIA' : formData.tipoQueja === 'anonima' ? 'DESCRIPCIÓN DE LA DENUNCIA' : 'DESCRIPCIÓN'}
                            </h4>
                            <div className="mb-3">
                                <label className="form-label">
                                    {formData.tipoQueja === 'identificada' ? 'Describa su sugerencia en detalle' : formData.tipoQueja === 'anonima' ? 'Describa su denuncia en detalle' : 'Describa en detalle'} <RequiredAsterisk />
                                </label>
                                <textarea
                                    className={`form-control ${hasSubmitted && errors.queja ? 'is-invalid' : ''}`}
                                    name="queja"
                                    value={formData.queja}
                                    onChange={handleInputChange}
                                    rows="6"
                                    placeholder={
                                        formData.tipoQueja === 'identificada'
                                            ? 'Por favor, describa su sugerencia de manera clara y detallada...'
                                            : formData.tipoQueja === 'anonima'
                                                ? 'Por favor, describa su denuncia de manera clara y detallada...'
                                                : 'Por favor, describa de manera clara y detallada...'
                                    }
                                />
                                {hasSubmitted && errors.queja && <div className="invalid-feedback">{errors.queja}</div>}
                                <small className="form-text text-muted">
                                    Mínimo 10 caracteres. Sea específico incluyendo fechas, personas involucradas y detalles relevantes.
                                </small>
                            </div>
                        </div>

                        <div className="mb-4 formacion-item-content border p-3 mb-3 rounded">
                            <h4 className="border-bottom pb-2">EVIDENCIA (OPCIONAL)</h4>
                            <div className="mb-3">
                                <label className="form-label">Adjuntar evidencia</label>
                                <input
                                    type="file"
                                    className={`form-control ${hasSubmitted && fileError ? 'is-invalid' : ''}`}
                                    name="evidencia"
                                    onChange={handleInputChange}
                                    accept="*/*"
                                />
                                {hasSubmitted && fileError && <div className="invalid-feedback d-block">{fileError}</div>}
                                <small className="form-text text-muted">
                                    Puede adjuntar cualquier tipo de archivo (.zip, .rar, imágenes, documentos, etc.). Tamaño máximo: 5MB
                                </small>
                                {formData.evidencia && !fileError && (
                                    <div className="mt-2 text-success">
                                        <small>Archivo seleccionado: {formData.evidencia.name} ({(formData.evidencia.size / 1024 / 1024).toFixed(2)} MB)</small>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="mb-4" ref={termsRef}>
                            <div className="box mt-2 p-2 text-content" style={{ backgroundColor: "#f8f9fa", border: "1px solid #ced4da", borderRadius: "5px" }}>
                                La información transmitida, incluidos los archivos adjuntos, está destinada únicamente a la(s) persona(s) o entidad a la que se dirige y puede contener material confidencial y/o privilegiado. Queda prohibida cualquier revisión, retransmisión, difusión u otro uso, o cualquier acción basada en esta información por parte de personas o entidades que no sean el destinatario previsto. Para acceder a nuestra Política de Tratamiento de Datos Personales <a href="https://www.conduto.com/images/dp/POLITICA-DE-TRATAMIENTO-DE-DATOS-PERSONALES.pdf" target="_blank" rel="noopener noreferrer">aquí</a>, y si desea comunicarse con nosotros tenga a su disposición el correo: <a href="mailto:datospersonales@conduto.com">datospersonales@conduto.com</a>
                            </div>
                            <br />
                            <div className="form-check">
                                <input
                                    style={{
                                        width: "18px",
                                        height: "18px",
                                        border: "2px solid #014993",
                                        backgroundColor: acceptedTerms ? "#014993" : "#eee",
                                        appearance: "none",
                                        cursor: "pointer"
                                    }}
                                    className={`form-check-input ${hasSubmitted && errors.terms ? 'is-invalid' : ''}`}
                                    type="checkbox"
                                    id="termsCheck"
                                    checked={acceptedTerms}
                                    onChange={(e) => {
                                        setAcceptedTerms(e.target.checked);
                                        if (hasSubmitted && errors.terms) {
                                            setErrors(prev => ({ ...prev, terms: '' }));
                                        }
                                    }}
                                />
                                <label className="form-check-label" htmlFor="termsCheck">
                                    Autorizo el tratamiento de mis datos personales a Conduto Ecuador S.A. conforme lo establecido en su política de privacidad <RequiredAsterisk />
                                </label>
                                {hasSubmitted && errors.terms && <div className="invalid-feedback d-block">{errors.terms}</div>}
                            </div>
                        </div>

                        {showSuccess && (
                            <div className="alert alert-success mt-3" role="alert">
                                {console.log('formData.tipoQueja en éxito:', formData.tipoQueja)}
                                <strong>¡Éxito!</strong> Su {tipoEnviado === 'identificada' ? 'sugerencia' : 'denuncia'} ha sido enviada correctamente.
                                {confirmationSentToUser && (
                                    <>
                                        <br />
                                        <small>Se ha enviado un correo de confirmación a la dirección proporcionada.</small>
                                    </>
                                )}
                                {!confirmationSentToUser && formData.tipoQueja === 'identificada' && (
                                    <>
                                        <br />
                                        <small>Nos pondremos en contacto si es necesario.</small>
                                    </>
                                )}
                                <button
                                    type="button"
                                    className="btn-close float-end"
                                    onClick={() => {
                                        setShowSuccess(false);
                                        setConfirmationSentToUser(false);
                                    }}
                                    aria-label="Cerrar"
                                ></button>
                            </div>
                        )}

                        <div className="d-grid gap-2">
                            <button
                                type="submit"
                                className="btn btn-primary btn-lg"
                                disabled={!acceptedTerms || isSubmitting}
                            >
                                {isSubmitting ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        Enviando...
                                    </>
                                ) : (
                                    formData.tipoQueja === 'identificada'
                                        ? 'Enviar Sugerencia'
                                        : formData.tipoQueja === 'anonima'
                                            ? 'Enviar Denuncia'
                                            : 'Enviar'
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            <style>
                {`
                    .error-highlight {
                        animation: error-pulse 2s ease-in-out;
                    }
                    
                    @keyframes error-pulse {
                        0% { box-shadow: 0 0 0 0 rgba(220, 53, 69, 0.7); }
                        70% { box-shadow: 0 0 0 10px rgba(220, 53, 69, 0); }
                        100% { box-shadow: 0 0 0 0 rgba(220, 53, 69, 0); }
                    }
                    
                    .formacion-item-content {
                        transition: all 0.3s ease;
                    }
                    
                    .formacion-item-content.is-invalid {
                        border-color: #dc3545 !important;
                    }
                `}
            </style>
        </div>
    );
};

export default SistemaQuejas;